import { memo, useEffect, useRef, useState } from 'react'
import { formatTimestamp } from '@vc/services/format'
import type { Participant } from '@vc/models'
// `playNotificationSound` was previously fired on local send; chat sounds are
// reserved for inbound messages and are handled by the receive path now.
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import type { ChatAttachmentKind, ChatMessage, ChatType } from '../models/chat.model'
import { ChatAttachmentBubble } from './ChatAttachmentBubble'
import { ChatComposer } from './ChatComposer'
import { ChatDisabledNotice } from './ChatDisabledNotice'
import { ChatTabs, type ChatTab } from './ChatTabs'
import { ParticipantSelector } from './ParticipantSelector'
import { PrivateChatList } from './PrivateChatList'
import { PrivateChatView } from './PrivateChatView'
import { saveChatAsHtml } from '../services/chat-html-export'
import { useChatStore } from '../store/chat.store'

interface ChatPanelProps {
  /** Group thread, off the room channel. */
  messages: ChatMessage[]
  /**
   * peerId → that private conversation. Lives in the chat store rather than in
   * this component: the panel is lazy-mounted, so state held here was thrown
   * away every time the user closed the sidebar mid-meeting.
   */
  privateThreads?: Record<string, ChatMessage[]>
  /** Unread in the group thread, for the Group tab's badge. */
  unreadGroup?: number
  /** peerId → unread count, for the badges in the private list. */
  unreadPrivate?: Record<string, number>
  localUserId: string
  canChat?: boolean
  /** Host-controls gate: when false, the private-chat tab is hidden. */
  canPrivateChat?: boolean
  /**
   * Webinar / host-only gate. When false (the default) the Broadcast tab is
   * hidden so regular meetings see only Group + Private. Callers running a
   * webinar or with an explicit broadcast permission should pass `true`.
   */
  canBroadcast?: boolean
  /** Host-controls gate: when false, the attach-files picker is hidden. */
  canSendFiles?: boolean
  /** Host-controls gate: when false, the emoji picker is hidden. */
  canUseEmojis?: boolean
  /**
   * Send a message. `to` is the recipient peerId for a private message, or
   * null for the whole room. `type` is kept for the broadcast tab.
   */
  onSend: (text: string, type: ChatType, to?: string | null) => void
  /** Upload + send a file to one thread. Rejects if the upload fails. */
  onAttach?: (file: File, kind: ChatAttachmentKind, to?: string | null) => Promise<void> | void
  /**
   * Mark everything read, everywhere. Kept in the contract for callers that
   * want a blunt reset (leaving the meeting, clearing chat) — the panel itself
   * no longer calls it, because "the user rendered a message" is not the same
   * as "the user read it": that was what cleared private badges while someone
   * was reading the group tab.
   */
  onMarkRead: () => void
  /**
   * Whether the panel is actually on screen.
   *
   * Not the same as "mounted": closing the sidebar animates it to width 0 and
   * translates it off, leaving this component mounted the whole time. Without
   * this flag the active thread stayed set after the sidebar closed, so every
   * later message counted as already-read and no badge ever appeared again.
   */
  visible?: boolean
  /**
   * Which conversation is on screen — the store uses it to decide what counts
   * as unread, so a message arriving in a thread you aren't reading keeps its
   * badge. `null` when the panel shows no thread (the private list).
   */
  onActiveThreadChange?: (thread: string | null) => void
  /** Wipe one conversation locally (the private view's "clear chat"). */
  onClearThread?: (thread: string) => void
  typingUsers?: string[]
  participants?: Participant[]
  initialSelectedPrivateChat?: string | null
  onShowToast?: (message: string, senderName: string) => void
}

export const ChatPanel = memo(function ChatPanel({
  messages,
  privateThreads = {},
  unreadGroup = 0,
  unreadPrivate = {},
  localUserId,
  canChat = true,
  canPrivateChat = true,
  canBroadcast = false,
  canSendFiles = true,
  canUseEmojis = true,
  visible = true,
  onSend,
  onAttach,
  // Deliberately unused here — see the prop's doc comment.
  onMarkRead: _onMarkRead,
  onActiveThreadChange,
  onClearThread,
  typingUsers = [],
  participants = [],
  initialSelectedPrivateChat,
  // onShowToast is kept in the props contract so callers can wire it later
  // for inbound-message notifications; we no longer fire it on local sends.
  onShowToast: _onShowToast,
}: ChatPanelProps) {
  const t = useTranslation()
  // `canChat` is the single send-allowed flag — callers AND it with
  // host-controls / webinar overrides before passing it in.
  const effectiveCanChat = canChat
  const privateChatAllowed = canPrivateChat
  const [text, setText] = useState('')
  const [activeTab, setActiveTab] = useState<ChatTab>('group')
  const [showParticipantSelector, setShowParticipantSelector] = useState(false)
  const [selectedPrivateChat, setSelectedPrivateChat] = useState<string | null>(null)
  const partnerNames = useChatStore((s) => s.partnerNames)
  const listRef = useRef<HTMLDivElement | null>(null)

  // Uploads then sends. Nothing is rendered optimistically: the file appears
  // when the message carrying it comes back off the channel, the same moment
  // everyone else sees it. The previous object-URL preview showed the sender a
  // file that had never left the browser.
  const handleAttach = (file: File, kind: ChatAttachmentKind) => {
    void onAttach?.(file, kind, null)
  }

  useEffect(() => {
    if (initialSelectedPrivateChat) {
      setActiveTab('private')
      setSelectedPrivateChat(initialSelectedPrivateChat)
    }
  }, [initialSelectedPrivateChat])

  // Closing private chat stops you SENDING, not reading, so the tab is always
  // there.
  //
  // Moderators are exempt from the gate on both sides — the server's
  // `chat_allowed?/2` lets a host DM an attendee whose private chat is closed —
  // and hiding the tab meant that message arrived and was unreadable. Showing
  // it only when a thread already exists was worse than it sounds: threads live
  // in memory, so the tab appeared on delivery and vanished on the next reload,
  // and the user was left refreshing at a surface that kept disappearing.
  // Unconditional is the only version with no state to get wrong.

  // Same guard for the broadcast tab — if the host leaves webinar mode or
  // revokes broadcast permission while the user is viewing it, swap them
  // back to Group rather than rendering an orphaned panel.
  useEffect(() => {
    if (!canBroadcast && activeTab === 'broadcast') {
      setActiveTab('group')
    }
  }, [canBroadcast, activeTab])

  // Tell the store which conversation is on screen, so it can decide what
  // counts as unread. The old code called `onMarkRead()` on every change of
  // `messages.length`, which cleared the badge for messages the user was not
  // looking at — including private ones while they read the group tab.
  const openThread =
    activeTab === 'private' ? selectedPrivateChat : activeTab === 'group' ? 'group' : null

  // Gated on `visible`, not on being mounted: the sidebar closes by animating
  // to zero width, so this component lives on behind it. Reporting the thread
  // as still-open there meant a closed panel silently absorbed every message.
  useEffect(() => {
    onActiveThreadChange?.(visible ? openThread : null)
    return () => onActiveThreadChange?.(null)
  }, [visible, openThread, onActiveThreadChange])

  // In a meeting only the latest messages matter, so the list follows every new
  // message and typing line, from anyone, even if the user had scrolled up.
  // Scrolling the list itself rather than scrollIntoView keeps the meeting
  // layout from being scrolled along with it. The private thread view does its
  // own scrolling.
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages.length, typingUsers.length])

  // Opening the panel or switching tabs lands on the latest message at once.
  useEffect(() => {
    const el = listRef.current
    if (visible && el) el.scrollTop = el.scrollHeight
  }, [visible, activeTab, selectedPrivateChat])

  const handleSend = () => {
    const trimmed = text.trim()
    if (!trimmed || !effectiveCanChat) return
    onSend(trimmed, activeTab === 'broadcast' ? 'broadcast' : 'active', null)
    // Toast + sound are intentionally NOT fired here — the user is the
    // sender and doesn't need to be notified of their own message. Inbound
    // messages from other participants notify via their own path.
    setText('')
  }

  const handlePrivateSend = (privateText: string, recipientId: string) => {
    // Straight onto the channel, addressed to one peer. The server delivers it
    // to the two of us and to nobody else, then echoes it back here — no local
    // copy, so what the sender sees is what was actually delivered.
    onSend(privateText, 'active', recipientId)
  }

  const handlePrivateAttach = (file: File, kind: ChatAttachmentKind, recipientId: string) => {
    void onAttach?.(file, kind, recipientId)
  }

  const handleSelectParticipant = (participant: Participant) => {
    if (!participant.isLocal) {
      setSelectedPrivateChat(participant.id)
    }
  }

  const handleClearPrivateChat = () => {
    if (!selectedPrivateChat || !onClearThread) return
    onClearThread(selectedPrivateChat)
    // Said, as Presence says it. The thread emptying was the only sign, and an
    // empty thread looks the same as one that never had anything in it.
    toast.success({ title: t('presence.chatCleared'), description: t('presence.chatClearedDesc') })
  }

  // A peer who has left is still worth showing: the conversation happened, and
  // the roster is not its history. So: the roster, else the name remembered
  // while they were here, else the newest message THEY sent. Never the last
  // message's sender as such — that was you whenever you wrote last, and the
  // thread was listed under your own name.
  const partnerNameOf = (peerId: string, msgs: ChatMessage[] | undefined): string => {
    const live = participants.find((p) => p.id === peerId)?.name
    if (live) return live
    const remembered = partnerNames[peerId]
    if (remembered) return remembered
    const theirs = [...(msgs ?? [])].reverse().find((m) => m.senderId === peerId && m.senderName && m.senderName !== peerId)
    return theirs?.senderName || t('panels.unknown')
  }

  const privateConversations = Object.entries(privateThreads)
    .filter(([, msgs]) => msgs.length > 0)
    .map(([participantId, msgs]) => {
      const lastMsg = msgs[msgs.length - 1]
      return {
        participantId,
        participantName: partnerNameOf(participantId, msgs),
        lastMessage: lastMsg.attachment ? lastMsg.attachment.name : lastMsg.text,
        timestamp: lastMsg.timestamp,
        unreadCount: unreadPrivate[participantId] ?? 0,
      }
    })
    .sort((a, b) => b.timestamp - a.timestamp)

  if (activeTab === 'private' && selectedPrivateChat) {
    // Fall back to a stub built from the thread when the peer has left, so an
    // open conversation doesn't collapse back to the list mid-read.
    const participant =
      participants.find((p) => p.id === selectedPrivateChat) ??
      (privateThreads[selectedPrivateChat]?.length
        ? {
            id: selectedPrivateChat,
            // The same lookup as the list: a thread in which only you wrote
            // has no message of theirs to read a name from, and said "Unknown".
            name: partnerNameOf(selectedPrivateChat, privateThreads[selectedPrivateChat]),
          }
        : null)
    if (participant) {
      return (
        <PrivateChatView
          participant={participant}
          messages={privateThreads[selectedPrivateChat] || []}
          localUserId={localUserId}
          onSend={handlePrivateSend}
          onAttach={handlePrivateAttach}
          // The gate has to reach the private composer too. It used to hardcode
          // `canChat={true}` inside PrivateChatView, so a thread opened before
          // the host closed private chat kept a working-looking composer: the
          // user typed, pressed send, and the server dropped it with nothing on
          // screen to say why.
          canChat={privateChatAllowed}
          canSendFiles={canSendFiles}
          canUseEmojis={canUseEmojis}
          onBack={() => setSelectedPrivateChat(null)}
          onSaveChat={() => {
            // Saving a chat produces the file — no format to pick, no second
            // dialog. The whole conversation goes out as a standalone .html
            // laid out the way the panel lays it out.
            saveChatAsHtml({
              title: t('panels.chatWith', { name: participant.name }) || participant.name,
              messages: privateThreads[selectedPrivateChat] || [],
              localUserId,
            })
          }}
          onClearChat={handleClearPrivateChat}
        />
      )
    }
  }

  // One source: the store's group thread, already time-ordered and deduped.
  const groupMessages = messages.filter((m) => m.type !== 'broadcast')
  const broadcastMessages = messages.filter((m) => m.type === 'broadcast')
  const isContentEmpty =
    (activeTab === 'private' && privateConversations.length === 0) ||
    (activeTab === 'group' && groupMessages.length === 0) ||
    (activeTab === 'broadcast' && broadcastMessages.length === 0)

  return (
    <div className="flex h-full flex-col bg-panelcolor text-vc-panel-text overflow-auto w-full">
      <ChatTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showPrivateTab
        showBroadcastTab={canBroadcast}
        unreadGroup={unreadGroup}
        unreadPrivate={Object.values(unreadPrivate).reduce((sum, n) => sum + n, 0)}
      />

      <div
        ref={listRef}
        className={`mobile-sheet-content flex-1 min-h-0 overflow-y-auto flex flex-col px-4 mobile-input-container ${
          isContentEmpty ? 'justify-center items-center' : 'py-3 space-y-3'
        }`}
      >
        {messages.length === 0 && activeTab === 'group' && (
          <div className="flex flex-col items-center justify-center text-center">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="35" cy="45" r="12" fill="#E8D5D0" />
              <path d="M20 75C20 61.19 30.19 50 44 50H26C16.59 50 8 58.59 8 68V75H20Z" fill="#E8D5D0" />
              <rect x="48" y="25" width="32" height="24" rx="4" fill="#F5C6C6" stroke="#E8A5A5" strokeWidth="1.5" />
              <rect x="52" y="31" width="20" height="2" rx="1" fill="#E8A5A5" />
              <rect x="52" y="37" width="16" height="2" rx="1" fill="#E8A5A5" />
              <circle cx="85" cy="45" r="12" fill="#E8D5D0" />
              <path d="M70 75C70 61.19 80.19 50 94 50H76C66.59 50 58 58.59 58 68V75H70Z" fill="#E8D5D0" />
              <rect x="40" y="60" width="32" height="24" rx="4" fill="#D4E8F5" stroke="#A5C8E8" strokeWidth="1.5" />
              <rect x="44" y="66" width="20" height="2" rx="1" fill="#A5C8E8" />
              <rect x="44" y="72" width="16" height="2" rx="1" fill="#A5C8E8" />
            </svg>
            <div className="text-lg font-medium text-slate-700 dark:text-foreground">
              {t('panels.noMessagesYet')}
            </div>
          </div>
        )}

        {activeTab === 'private' && (
          <PrivateChatList
            conversations={privateConversations}
            onSelectConversation={setSelectedPrivateChat}
            // Reading is still allowed; starting a new thread is not. Omitting
            // the callback drops the compose button rather than offering one
            // that opens a picker leading to a composer that refuses to send.
            onNewChat={privateChatAllowed ? () => setShowParticipantSelector(true) : undefined}
          />
        )}

        {messages.length === 0 && activeTab === 'broadcast' && (
          <div className="flex flex-col items-center justify-center text-center">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="60" cy="45" r="12" fill="#E8D5D0" />
              <path d="M45 75C45 61.19 55.19 50 69 50H51C41.59 50 33 58.59 33 68V75H45Z" fill="#E8D5D0" />
              <path d="M75 45C85 45 92.5 52.5 92.5 62.5" stroke="#F5C6C6" strokeWidth="2" fill="none" strokeDasharray="3 2" />
              <path d="M80 40C95 40 105 50 105 65" stroke="#F5C6C6" strokeWidth="2" fill="none" strokeDasharray="3 2" />
              <path d="M85 35C105 35 117.5 47.5 117.5 67.5" stroke="#F5C6C6" strokeWidth="2" fill="none" strokeDasharray="3 2" />
              <rect x="55" y="55" width="10" height="15" rx="2" fill="#4A90E2" />
              <path d="M52 65C52 68.31 54.69 71 58 71H62C65.31 71 68 68.31 68 65" stroke="#4A90E2" strokeWidth="2" fill="none" />
              <line x1="60" y1="73" x2="60" y2="76" stroke="#4A90E2" strokeWidth="2" />
            </svg>
            <div className="text-lg font-medium text-slate-700 dark:text-foreground">
              {t('panels.noBroadcastsYet')}
            </div>
            <div className="text-sm text-slate-400 dark:text-muted-foreground">
              {t('panels.sendAnnouncementsToAll')}
            </div>
          </div>
        )}

        {activeTab === 'group' &&
          groupMessages.map((msg, idx, arr) => {
            const isMe = msg.senderId === localUserId
            const prevMsg = idx > 0 ? arr[idx - 1] : null
            const showName = !isMe && (!prevMsg || prevMsg.senderId !== msg.senderId)
            // Every message carries its own timestamp — collapsing them by
            // 5-minute groups confused users who couldn't tell when each
            // message was sent.
            return (
              <div key={msg.id} className={['flex flex-col gap-0.5', isMe ? 'items-end' : 'items-start'].join(' ')}>
                {showName && (
                  <div className="text-[11px] font-semibold text-slate-800 dark:text-foreground">
                    {msg.senderName}
                  </div>
                )}
                {msg.attachment ? (
                  <>
                    <ChatAttachmentBubble attachment={msg.attachment} isMe={isMe} />
                    {/* ChatAttachmentBubble has its own per-kind layout (image/video/
                        audio/document) with no slot for a timestamp, so this stays
                        a trailing caption rather than going inside it. */}
                    <div className="text-[10px] text-slate-400 dark:text-muted-foreground">
                      {formatTimestamp(msg.timestamp)}
                    </div>
                  </>
                ) : (
                  // Time sits inside the bubble, flowing after the text, not as a
                  // sibling line below it — a flex-wrap item (not `float` or plain
                  // inline) so it still counts toward the bubble's measured size:
                  // floated or out-of-flow, a one-line message left the bubble too
                  // narrow and the time hung off the edge; inline with no break
                  // opportunity, a full-width line pushed it past the right edge.
                  // `ml-auto` pins it to the end of whichever line it lands on.
                  <div
                    className={[
                      'max-w-[85%] min-w-0 overflow-hidden rounded-2xl px-3 py-2 text-sm leading-relaxed',
                      isMe
                        ? 'rounded-br-sm bg-primary text-white'
                        : 'rounded-bl-sm bg-slate-100 dark:bg-muted text-slate-800 dark:text-foreground',
                    ].join(' ')}
                  >
                    <div className="flex flex-wrap items-end gap-x-2">
                      <span className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                        {msg.text}
                      </span>
                      <span
                        className={[
                          'ml-auto shrink-0 whitespace-nowrap text-[10px] leading-none',
                          isMe ? 'text-primary-foreground/70' : 'text-slate-400 dark:text-muted-foreground',
                        ].join(' ')}
                      >
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )
          })}

        {activeTab === 'broadcast' &&
          broadcastMessages.map((msg) => {
            const isMe = msg.senderId === localUserId
            // Every broadcast message shows its own time — same rationale
            // as the group tab.
            return (
              <div key={msg.id} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[11px] font-bold text-red-600 uppercase tracking-wide">
                    {t('panels.broadcast')}
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-muted-foreground">
                    {formatTimestamp(msg.timestamp)}
                  </span>
                </div>
                <div className={['flex gap-2', isMe ? 'justify-end' : 'justify-start'].join(' ')}>
                  {!isMe && (
                    <div className="text-[11px] font-semibold text-slate-800 dark:text-foreground">
                      {msg.senderName}
                    </div>
                  )}
                  {msg.attachment ? (
                    <ChatAttachmentBubble attachment={msg.attachment} isMe={isMe} />
                  ) : (
                    <div
                      className={[
                        'max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed border-2',
                        isMe
                          ? 'rounded-br-sm bg-red-50 dark:bg-red-900 border-red-200 text-red-900'
                          : 'rounded-bl-sm bg-red-50 dark:bg-red-900 border-red-200 text-red-900',
                      ].join(' ')}
                    >
                      {msg.text}
                    </div>
                  )}
                  {isMe && (
                    <div className="text-[11px] font-semibold text-slate-800 dark:text-foreground">
                      {t('panels.you')}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

        {typingUsers.length > 0 && activeTab === 'group' && (
          <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-muted-foreground italic">
            <span>
              {typingUsers.join(', ')}{' '}
              {typingUsers.length === 1 ? t('panels.isTyping') : t('panels.areTyping')}
            </span>
            <span className="inline-flex gap-0.5">
              <span className="animate-pulse">●</span>
              <span className="animate-pulse delay-75">●</span>
              <span className="animate-pulse delay-150">●</span>
            </span>
          </div>
        )}
      </div>

      {/* The private LIST has no composer, so the refusal has nowhere to live
          unless it is rendered here. Without it the list looked entirely
          normal and the user only discovered the gate after opening a thread. */}
      {activeTab === 'private' && !privateChatAllowed && <ChatDisabledNotice isPrivate />}

      {activeTab !== 'private' && (
        <ChatComposer
          text={text}
          onTextChange={setText}
          canChat={effectiveCanChat}
          isBroadcast={activeTab === 'broadcast'}
          onSend={handleSend}
          onAttach={handleAttach}
          canAttachFiles={canSendFiles}
          canUseEmojis={canUseEmojis}
        />
      )}

      {showParticipantSelector && (
        <ParticipantSelector
          participants={participants}
          onSelectParticipant={handleSelectParticipant}
          onClose={() => setShowParticipantSelector(false)}
        />
      )}
    </div>
  )
})
